import { Button, Group, Text, Textarea } from '@mantine/core';
import { useState } from 'react';
import { useShiftAnswerMutation } from '../api/hooks';
import type { ShiftAnswerView } from '../api/types';
import { useCoordinatorLabel } from '../lib/people';

export const SHIFT_ANSWER_LABEL: Record<ShiftAnswerView['status'] | 'none', string> = {
  ok: 'izmena potrjena',
  change: 'predlaga spremembo',
  cant: 'ne more',
  none: 'brez odgovora',
};

const DOT: Record<ShiftAnswerView['status'] | 'none', string> = {
  ok: 'potrjen',
  change: 'zavrnjen',
  cant: 'zavrnjen',
  none: 'caka',
};

/**
 * What the person said about this one shift, and — for whoever may write the
 * person — recording an answer for it by phone or taking one back (SPEC §1.1F).
 * Every action here writes this shift alone; the rest of their schedule is
 * answered shift by shift, or confirmed on their profile.
 *
 * `onReplace` opens the shift's `Oseba` picker to find somebody else, leaving
 * the shift as it is until that is saved; it is offered on a refusal to
 * whoever may also change the plan.
 */
export function ShiftAnswerBox({
  volunteerId,
  shiftId,
  answer,
  canWrite,
  onReplace,
}: {
  volunteerId: string;
  shiftId: string;
  answer: ShiftAnswerView | undefined;
  canWrite: boolean;
  onReplace?: () => void;
}) {
  const mut = useShiftAnswerMutation();
  const label = useCoordinatorLabel();
  const [refusing, setRefusing] = useState(false);
  const [comment, setComment] = useState('');
  const status = answer?.status ?? 'none';
  const refused = status === 'cant' || status === 'change';
  // Who gave it: a coordinator by name, the volunteer on their page. A carried
  // answer restates an earlier one and names nobody.
  const by =
    answer && !answer.carried
      ? answer.actorType === 'coordinator'
        ? label(answer.actorId)
        : answer.actorType === 'volunteer'
          ? 'na strani'
          : ''
      : status === 'none'
        ? 'na to izmeno'
        : '';

  return (
    <div className={`tl-detail-answer tl-detail-answer--${status}`}>
      <Group gap={6}>
        <span className={`tl-status-dot tl-status-dot--${DOT[status]}`} />
        <Text size="sm" fw={600}>
          {SHIFT_ANSWER_LABEL[status]}
        </Text>
        {by ? (
          <Text size="xs" c="dimmed">
            {by}
          </Text>
        ) : null}
      </Group>
      {answer?.comment ? <div className="tl-detail-quote">„{answer.comment}“</div> : null}

      {canWrite && refusing ? (
        <>
          <Textarea
            size="xs"
            placeholder="Pojasnilo osebe — npr. „To izmeno ne morem, drugo pa lahko.“"
            value={comment}
            onChange={(e) => setComment(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group gap={6}>
            <Button
              size="compact-sm"
              color="red"
              variant="light"
              loading={mut.isPending}
              onClick={() =>
                mut.mutate(
                  { volunteerId, shiftId, status: 'cant', comment },
                  {
                    onSuccess: () => {
                      setRefusing(false);
                      setComment('');
                    },
                  },
                )
              }
            >
              Zabeleži »ne more«
            </Button>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={() => setRefusing(false)}>
              Prekliči
            </Button>
          </Group>
        </>
      ) : canWrite ? (
        <Group gap={6}>
          {refused && onReplace ? (
            <Button size="compact-sm" style={{ background: 'oklch(0.35 0.05 245)' }} onClick={onReplace}>
              Poišči zamenjavo
            </Button>
          ) : null}
          {status !== 'ok' ? (
            <Button
              size="compact-sm"
              variant="light"
              color="green"
              loading={mut.isPending}
              onClick={() => mut.mutate({ volunteerId, shiftId, status: 'ok' })}
            >
              {refused ? 'Vseeno potrdi' : 'Potrdi to izmeno'}
            </Button>
          ) : null}
          {/* A confirmed shift or a proposed change can still turn into "ne more"
              on the phone, without taking the answer back first. */}
          {status !== 'cant' ? (
            <Button size="compact-sm" variant="default" onClick={() => setRefusing(true)}>
              Ne more…
            </Button>
          ) : null}
          {answer ? (
            <Button
              size="compact-sm"
              variant="subtle"
              color="gray"
              loading={mut.isPending}
              onClick={() => mut.mutate({ volunteerId, shiftId, status: 'pending' })}
            >
              {status === 'ok' ? 'Počisti odgovor' : 'Počisti'}
            </Button>
          ) : null}
        </Group>
      ) : null}
    </div>
  );
}
